import type React from 'react';
import { Flex, Avatar } from '@nula/core-ui';
import { speakerColor } from './speaker-color';
import styles from './speaker-avatar.module.css';

export interface SpeakerAvatarProps {
  speaker: string;
  /** Attendee profile photo, when known. Falls back to the colored initial. */
  photoUrl?: string;
  /** While true, show a pulsing skeleton instead of the initial — used when the
   * attendee (name/photo) for a labeled speaker is still resolving. */
  loading?: boolean;
  className?: string;
}

/**
 * Round speaker avatar. Shows the attendee's profile photo when `photoUrl` is
 * provided (core-ui Avatar handles load-error fallback); otherwise a
 * color-coded initial using the deterministic `speakerColor()` token (passed
 * via the `--avatar-bg` custom property so the CSS module owns the visual).
 */
export function SpeakerAvatar({ speaker, photoUrl, loading, className }: SpeakerAvatarProps) {
  const trimmed = speaker.trim();
  const initial = trimmed ? trimmed[0]!.toUpperCase() : '?';
  const label = trimmed || 'Unknown speaker';

  if (loading) {
    return (
      <Flex
        aria-hidden
        data-testid="speaker-avatar-loading"
        className={className ? `${styles.avatar} ${styles.loading} ${className}` : `${styles.avatar} ${styles.loading}`}
      />
    );
  }

  if (photoUrl) {
    return (
      <Avatar
        src={photoUrl}
        alt={label}
        size="lg"
        className={className}
      >
        {initial}
      </Avatar>
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      aria-label={label}
      className={className ? `${styles.avatar} ${className}` : styles.avatar}
      style={{ '--avatar-bg': speakerColor(speaker) } as React.CSSProperties}
    >
      {initial}
    </Flex>
  );
}
